import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { ApiError } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import { toast } from '@/hooks/useToast';
import { declinePrescriptionItem } from './api';
import type { DeclineReason, DispensablePrescriptionItem, ItemDispenseStatus, PrescriptionDispenseStatus } from './types';

export const DECLINE_REASONS: { value: DeclineReason; label: string }[] = [
  { value: 'patient_declined', label: 'Patient doesn’t want it' },
  { value: 'bought_elsewhere', label: 'Buying it elsewhere' },
  { value: 'not_available', label: 'Not available here' },
  { value: 'cost', label: 'Cost' },
  { value: 'other', label: 'Other' },
];
export const reasonLabel = (r: string) => DECLINE_REASONS.find((x) => x.value === r)?.label ?? r;

const ITEM_STATUS: Record<ItemDispenseStatus, { label: string; tone: 'neutral' | 'info' | 'success' | 'warn' }> = {
  not_dispensed: { label: 'Not dispensed', tone: 'neutral' },
  partially_dispensed: { label: 'Partly dispensed', tone: 'info' },
  fully_dispensed: { label: '✓ Dispensed', tone: 'success' },
  declined: { label: 'Rest declined', tone: 'warn' },
  open_ended: { label: 'As needed', tone: 'neutral' },
};
const RX_STATUS: Record<PrescriptionDispenseStatus, { label: string; tone: 'neutral' | 'info' | 'success' | 'warn' | 'danger' }> = {
  not_dispensed: { label: 'Not dispensed', tone: 'neutral' },
  partially_dispensed: { label: 'Partly dispensed', tone: 'info' },
  fully_dispensed: { label: 'Fully dispensed', tone: 'success' },
  completed_with_declines: { label: 'Completed — part declined', tone: 'warn' },
  declined: { label: 'Declined', tone: 'warn' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
};

export function ItemStatusBadge({ status }: { status?: ItemDispenseStatus }) {
  if (!status) return null;
  const s = ITEM_STATUS[status];
  return <Badge variant={s.tone}>{s.label}</Badge>;
}
export function PrescriptionStatusBadge({ status }: { status?: PrescriptionDispenseStatus }) {
  if (!status) return null;
  const s = RX_STATUS[status];
  return <Badge variant={s.tone}>{s.label}</Badge>;
}

/**
 * "Patient declined…" on a prescription line (G9): the rest won't be dispensed
 * now. Closes the line without a sale; can be undone if they come back.
 */
export function DeclineDialog({ item, prescriptionId, onClose }: { item: DispensablePrescriptionItem | null; prescriptionId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState<DeclineReason | ''>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item) { setQty(String(item.remaining_quantity ?? '')); setReason(''); setNote(''); setError(null); }
  }, [item]);

  const save = useMutation({
    mutationFn: () => declinePrescriptionItem(item!.id, { quantity: Number(qty), reason: reason as DeclineReason, note: note.trim() || undefined }),
    onSuccess: () => {
      toast({ title: `${qty} declined — line closed`, variant: 'success' });
      qc.invalidateQueries({ queryKey: keys.prescriptions.detail(prescriptionId) });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError || e instanceof Error ? e.message : 'Not saved'),
  });

  const submit = () => {
    const n = Number(qty);
    if (!Number.isInteger(n) || n <= 0) { setError('Enter a whole number of units'); return; }
    if (item?.remaining_quantity != null && n > item.remaining_quantity) { setError(`Only ${item.remaining_quantity} remain`); return; }
    if (!reason) { setError('Choose a reason'); return; }
    if (reason === 'other' && !note.trim()) { setError('Describe why'); return; }
    setError(null);
    save.mutate();
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-105">
        <DialogHeader>
          <DialogTitle>Patient declined the rest</DialogTitle>
          <DialogDescription>
            {item ? `${item.drug_name}${item.strength ? ` ${item.strength}` : ''} — ${item.remaining_quantity} still to dispense.` : ''}
            {' '}The line is closed without a sale. It can be re-opened if they come back.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-[110px_1fr] gap-3">
          <FormField label="Units declined"><Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} /></FormField>
          <FormField label="Reason">
            <NativeSelect value={reason} onChange={(e) => setReason(e.target.value as DeclineReason)}>
              <option value="">Choose…</option>
              {DECLINE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </NativeSelect>
          </FormField>
        </div>
        <FormField label={reason === 'other' ? 'Note' : 'Note (optional)'}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Will buy the remaining strips next week" />
        </FormField>
        {error && <div className="rounded-(--radius) bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={save.isPending}>Record decline</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
